import React, { useEffect, useMemo, useRef } from 'react'
import ReactDOM from 'react-dom'
import Snackbar from '@material-ui/core/Snackbar'
import { makeStyles, Slide } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import classNames from 'classnames'
import type { StyleClasses } from '../../styles'
import HorizontalScroller from '../HorizontalScroller'
import type { BulkActionButtonProps } from '../BulkActionButton'
import BulkActionButton from '../BulkActionButton'

const useStyles = makeStyles<Theme, BulkActionBarProps>(
  (theme) => ({
    root: {
      bottom: 0
    },
    belowBackdrop: {
      zIndex: 1300
    },
    snackbar: {
      height: theme.typography.pxToRem(60),
      width: '100vw',
      background: theme.palette.primary.main,
      color: theme.palette.primary.contrastText
    },
    content: {
      margin: '0 auto',
      height: '100%',
      display: 'flex',
      flexDirection: 'row' as const,
      justifyContent: 'space-around',
      alignItems: 'center',
      width: '80vw',
      [theme.breakpoints.down('md')]: {
        width: '100vw'
      }
    },
    selectedLabelWrapper: {
      flexGrow: 1,
      width: theme.typography.pxToRem(175),
      display: 'flex',
      justifyContent: 'flex-start',
      '@media (max-width: 599px)': {
        display: 'none'
      }
    },
    selectedLabel: {
      ...theme.typography.body1,
      color: 'white',
      marginLeft: theme.spacing(1),
      padding: theme.spacing(0, 2)
    },
    horizontalScroller: {
      flexGrow: 2,
      overflowX: 'hidden'
    },
    scrollButton: {
      color: theme.palette.primary.contrastText
    },
    horizontalScrollerContent: {
      height: '100%',
      display: 'flex',
      flexDirection: 'row' as const,
      alignItems: 'center',
      // justifyContent as 'center' will cause the scroller to not show all elements on small screens
      // so using margin to center works when we use a div inside of HorizontalScrollbar
      margin: '0 auto'
    },
    deselectWrapper: {
      flexGrow: 1,
      width: theme.typography.pxToRem(175),
      display: 'flex',
      justifyContent: 'flex-end',
      '@media (max-width: 599px)': {
        display: 'none'
      }
    }
  }),
  { name: 'BulkActionBar' }
)

export interface BulkActionBarProps {
  itemName: string
  pluralItemName?: string
  selectedItems?: any[]
  onDeselectAll?: () => void
  children?: Array<React.ReactElement<BulkActionButtonProps>> | React.ReactElement<BulkActionButtonProps>
  aboveBackdrop?: boolean
  classes?: StyleClasses<typeof useStyles>
}

function BulkActionBar(props: BulkActionBarProps) {
  const { itemName, pluralItemName, selectedItems, onDeselectAll, aboveBackdrop, children } = props
  const classes = useStyles(props)
  const rootDiv = useRef<HTMLDivElement>(document.createElement('div'))
  const snackbarRef = useRef<HTMLDivElement>(null)

  // Mount/unmount portal container
  useEffect(() => {
    document.body.appendChild(rootDiv.current)
    return () => {
      document.body.removeChild(rootDiv.current)
    }
  }, [])

  // Keep the bar pinned to the *visual* viewport bottom while scrolling.
  // On iOS a `position: fixed; bottom: 0` element is anchored to the layout
  // viewport, which sits below the visible area while the browser toolbar is
  // shown, so the bar drifts mid-scroll (#21177). Track visualViewport and
  // offset `bottom` by the layout/visual gap. No-ops on desktop (gap ≈ 0).
  // (`snackbarRef` lands on MUI's forwardRef Snackbar root <div>, the fixed
  // element carrying `bottom`; the Slide transition only moves the inner
  // content, so writing `bottom` here doesn't fight the animation.)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) {
      return
    }
    let frame = 0
    // Last committed bottom offset. While the page is rubber-band overscrolling
    // we hold this value instead of recomputing, so the bar rides *with* the
    // page during the bounce rather than fighting the compositor (#21177).
    let lastOffset = -1
    const update = () => {
      frame = 0
      if (!snackbarRef.current) {
        return
      }
      // During an iOS rubber-band bounce the document is scrolled past its real
      // bounds — negative at the top, beyond (scrollHeight - innerHeight) at the
      // bottom. visualViewport scroll events fire a frame late with noisy
      // height/offsetTop values, so any `bottom` we compute mid-bounce lags the
      // visual position and reads as jitter. Detect that state and hold the last
      // committed offset until scrolling settles back within bounds.
      const scrollTop = window.scrollY
      const maxScrollTop = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
      const isOverscrolling = scrollTop < 0 || scrollTop > maxScrollTop
      if (isOverscrolling) {
        return
      }
      // Round so sub-pixel visualViewport lag never produces a paint, and skip
      // the style write when the offset is effectively unchanged.
      const offset = Math.round(Math.max(0, window.innerHeight - vv.height - vv.offsetTop))
      if (offset === lastOffset) {
        return
      }
      lastOffset = offset
      snackbarRef.current.style.bottom = `${offset}px`
    }
    // visualViewport scroll/resize fire at high frequency during iOS rubber-band
    // scrolling, so coalesce updates to one style write per animation frame.
    const onViewportChange = () => {
      if (!frame) {
        frame = requestAnimationFrame(update)
      }
    }
    vv.addEventListener('scroll', onViewportChange)
    vv.addEventListener('resize', onViewportChange)
    return () => {
      if (frame) {
        cancelAnimationFrame(frame)
      }
      vv.removeEventListener('scroll', onViewportChange)
      vv.removeEventListener('resize', onViewportChange)
    }
  }, [])

  // Push Intercom behind the bar
  useEffect(() => {
    const openIntercom = document.querySelector<HTMLElement>('#intercom-container > div > iframe')
    const closedIntercom = document.querySelector<HTMLElement>('body > div.intercom-lightweight-app')
    if (openIntercom) openIntercom.style.zIndex = '1299'
    if (closedIntercom) closedIntercom.style.zIndex = '1299'
  }, [selectedItems])

  const handleActionButtonClick = (child: React.ReactElement<BulkActionButtonProps>) => {
    if (child?.props?.onClick) {
      child.props.onClick(selectedItems)
    }
  }

  const actionButtons = useMemo(
    () =>
      React.Children.map(
        children,
        (child: React.ReactElement<BulkActionButtonProps>) =>
          child &&
          React.cloneElement(child, {
            onClick: () => handleActionButtonClick(child)
          })
      ),
    [children, selectedItems]
  )

  const getSelectedLabel = () => {
    const numSelected = selectedItems ? selectedItems.length : 0
    let plural = pluralItemName

    if (!pluralItemName) {
      plural = itemName.toLowerCase().endsWith('s') ? `${itemName}'` : `${itemName}s`
    }

    return `${numSelected} ${numSelected === 1 ? itemName : plural} selected`
  }

  return ReactDOM.createPortal(
    <Snackbar
      ref={snackbarRef}
      classes={{
        root: classNames({
          [classes.root]: true,
          [classes.belowBackdrop]: !aboveBackdrop
        })
      }}
      open={!!(selectedItems && selectedItems.length > 0)}
      TransitionComponent={Slide}
    >
      <div className={classes.snackbar}>
        <div className={classes.content}>
          <div className={classes.selectedLabelWrapper}>
            <div className={classes.selectedLabel}>{getSelectedLabel()}</div>
          </div>
          <HorizontalScroller
            classes={{
              root: classes.horizontalScroller,
              scrollButton: classes.scrollButton
            }}
          >
            <div className={classes.horizontalScrollerContent}>{actionButtons}</div>
          </HorizontalScroller>
          <div className={classes.deselectWrapper}>
            <BulkActionButton onClick={onDeselectAll}>Deselect All</BulkActionButton>
          </div>
        </div>
      </div>
    </Snackbar>,
    rootDiv.current
  )
}

export default BulkActionBar
